import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  sortNameAsc,
  getLogoAssetObject,
  getLogoAltText,
} from '../utils/sharedFunc';

import { isCustomer } from '../utils/utils';

import useFetchAllCategories from './data/useFetchAllCategories';
import useWithGA from './useWithGA';

import CONSTANTS_COMPANY_SIZE from '../constants/companySize';

const sortBwScore = (a, b) => {
  const isACustomer = isCustomer(a.systemSiteDataForLocale.customerType);
  const isBCustomer = isCustomer(b.systemSiteDataForLocale.customerType);
  const aRatingBW = Number(a.ratingBW) || 0;
  const bRatingBW = Number(b.ratingBW) || 0;
  // Sort by customers type and then ratingBW
  if (isACustomer && aRatingBW > 0 && isBCustomer && bRatingBW > 0) {
    return bRatingBW - aRatingBW;
  }
  if (isACustomer && aRatingBW > 0) {
    return -1;
  }
  if (isBCustomer && bRatingBW > 0) {
    return 1;
  }
  if (!isACustomer && aRatingBW > 0 && !isBCustomer && bRatingBW > 0) {
    return bRatingBW - aRatingBW;
  }
  if (!isACustomer && aRatingBW > 0) {
    return -1;
  }
  if (!isBCustomer && bRatingBW > 0) {
    return 1;
  }
  return 0;
};

// const sortRatingBW = (a, b) => Number(b.ratingBW) - Number(a.ratingBW);

// const sortReviews = (a, b) => Number(b.reviews) - Number(a.reviews);

const sortRatingPopularity = (a, b) =>
  Number(b.ratingPopularity) - Number(a.ratingPopularity);

const sortSystemsAsc = (sysA, sysB) => {
  const { systemSiteDataForLocale: sysASiteData } = sysA;
  const { systemSiteDataForLocale: sysBSiteData } = sysB;

  return sortNameAsc(sysASiteData, sysBSiteData);
};

const getInitSystemsData = (systemsData) => {
  if (!systemsData) {
    return [];
  }

  return systemsData
    .filter((systemObj) => {
      const { systemSiteDataForLocale } = systemObj;
      return systemSiteDataForLocale.systemName.length > 0;
    })
    .map((systemObj) => {
      const { systemSiteDataForLocale } = systemObj;
      const logoAsset = getLogoAssetObject(systemSiteDataForLocale.mediaAssets);

      const updatedSystemObj = {
        ...systemObj,
        logo: logoAsset,
        logoAlt: getLogoAltText(
          logoAsset,
          systemObj.systemSiteDataForLocale.systemName
        ),
      };

      if (systemObj.id === 'ckfjrz4yc01ig0744aufjiiyq') {
        updatedSystemObj.followLink = true;
      }
      return updatedSystemObj;
    })
    .sort(sortSystemsAsc)
    .sort(sortBwScore);
};

/**
 *
 * @param {Object} excludedFilters [<filter>]
 * -------------------------------------------------
 */
export default function useWithCategory(
  category,
  systemsData,
  systemProps,
  excludedFilters
) {
  const { handleDataLayer } = useWithGA();

  const allSystems = useMemo(
    () => getInitSystemsData(systemsData),
    [systemsData]
  );
  const [compare, setCompare] = useState([]);
  const [filters, setFilters] = useState([]);
  const [activeFilters, setActiveFilters] = useState({});
  const [sorting, setSorting] = useState('rating');
  const [systems, setSystems] = useState(allSystems);
  const [subCatFilter, setSubCatFilter] = useState(null);
  const { allCategories } = useFetchAllCategories();

  // Init filters
  useEffect(() => {
    const selectCategory = category;

    // TODO: Translate this
    const filterObj = {
      clientCompanySize: {
        filterField: 'clientCompanySize',
        description: 'guideHome.formCompanySizeHelp',
        filterFields: ['MICRO', 'SMALL', 'MEDIUM', 'LARGE'],
        filterFieldsLocaleString: [
          CONSTANTS_COMPANY_SIZE.MICRO_SHORT,
          CONSTANTS_COMPANY_SIZE.SMALL_SHORT,
          CONSTANTS_COMPANY_SIZE.MEDIUM_SHORT,
          CONSTANTS_COMPANY_SIZE.LARGE_SHORT,
        ],
        filterName: 'clientCompanySize',
        filterNameNice: 'common.companySize',
        filterType: 'RADIO',
        isEnum: true,
        isOpenDefault: true,
        id: 'clientCompanySize',
      },
      ratingBW: {
        filterField: 'ratingBW',
        filterFields: ['1', '2', '3', '4'],
        filterFieldsNice: ['1+', '2+', '3+', '4+'],
        filterName: 'ratingBW',
        filterNameNice: 'common.rating',
        filterType: 'RADIO',
        id: 'ratingBW',
      },
      deployment: {
        filterField: 'deployment',
        filterFields: ['SAAS', 'ONPREMISE'],
        filterFieldsLocaleString: [
          'catFilter.filterCloud',
          'catFilter.filterOnpremise',
        ],
        filterName: 'deployment',
        filterNameNice: 'spQf.deployment',
        filterType: 'CHECKBOX',
        isEnum: true,
        sort: true,
        id: 'deployment',
      },
      price: {
        filterField: 'price',
        filterFields: ['PRICESMALL', 'PRICEMEDIUM', 'PRICELARGE'],
        filterFieldsNice: ['$', '$$', '$$$'],
        filterName: 'price',
        filterNameNice: 'common.price',
        filterType: 'RADIO',
        // isEnum: true,
        id: 'price',
      },
      erpBranches: {
        filterField: 'branch',
        filterFields: [
          'MANUFACTURING_PRODUCTION',
          'TRANSPORT_DISTRIBUTION',
          'SERVICE_AFTER_SALES',
          'RETAIL_WHOLESALE',
          'CONSULTANT_CONSULTING',
          'BANKING_FINANCE_INSURANCE',
          'HEALTH_SOCIAL_CARE',
        ],
        filterName: 'branch',
        filterNameNice: 'spQf.industries',
        filterType: 'CHECKBOX',
        isEnum: true,
        sort: true,
        id: 'branch',
      },
      cashRegisterBranches: {
        filterField: 'branch',
        filterFields: [
          'RETAIL_WHOLESALE',
          'RETAIL_FRANCHISE',
          'HOTEL_AND_RESTAURANT',
        ],
        filterName: 'branch',
        filterNameNice: 'spQf.industries',
        filterType: 'CHECKBOX',
        isEnum: true,
        sort: true,
        id: 'branch',
      },
    };

    const defaultFilterNames = ['clientCompanySize']; // 'ratingBW'
    const defaultFilters = defaultFilterNames.map(
      (filterName) => filterObj[filterName]
    );

    const categorySpecificFilterNames = {
      CRM: ['ratingBW', 'deployment'],
      PAYROLL: ['ratingBW', 'deployment'],
      HR: ['ratingBW', 'deployment'],
      TIMEREPORT: ['ratingBW'],
      LMS: ['ratingBW'],
      ISSUE_TRACKING: ['ratingBW'],
      RECRUITMENT: ['ratingBW', 'deployment'],
      ERP: ['deployment', 'erpBranches'],
      PROJECT_MANAGEMENT: ['ratingBW', 'deployment'],
      CASH_REGISTER: ['deployment', 'cashRegisterBranches'],
    };

    const categorySpecificFilter = [];

    const filterFields = systemProps
      .filter((propObj) => propObj.filter)
      .map((propObj) => propObj.dbName);

    categorySpecificFilterNames[selectCategory]?.forEach((filterName) => {
      categorySpecificFilter.push(filterObj[filterName]);
    });

    switch (selectCategory) {
      case allCategories?.CRM?.categoryDbName:
        categorySpecificFilter.push({
          id: 'productFirst',
          category: selectCategory,
          isEnum: true,
          filterName: 'productFirst',
          filterNameNice: 'catFilter.productFunctions',
          filterType: 'CHECKBOX',
          filterFields: [...filterFields],
          sort: true,
          isOpenDefault: true,
        });

        categorySpecificFilter.push({
          id: 'productSecond',
          category: selectCategory,
          isEnum: true,
          filterName: 'productSecond',
          filterNameNice: 'catFilter.specialFunctions',
          filterType: 'CHECKBOX',
          filterFields: [
            'marketingAutomation',
            'salesGamification',
            'leadScore',
            'dashboard',
          ],
          sort: true,
          isOpenDefault: true,
        });
        break;

      case allCategories?.PAYROLL?.categoryDbName:
        categorySpecificFilter.push({
          id: 'sector',
          filterFields: ['privateSector', 'publicSector'],
          filterType: 'CHECKBOX',
          filterName: 'sector',
          isEnum: true,
          filterNameNice: 'catFilter.privatePublicSector',
        });

        categorySpecificFilter.push({
          id: 'mustHaveOrAdd',
          filterFields: [...filterFields],
          filterType: 'CHECKBOX',
          filterName: 'mustHaveOrAdd',
          isEnum: true,
          isOpenDefault: true,
          category: selectCategory,
          filterNameNice: 'catFilter.productFuncAndServices',
        });
        break;

      case allCategories?.HR?.categoryDbName:
        categorySpecificFilter.push({
          id: 'modularFunc',
          filterFields: [
            'competence',
            'learningManagement',
            'compensationManagement',
            'performanceManagement',
            'successionPlanning',
            'hrAnalytics',
            'onboarding',
            'ownPayrollSystem',
            'recruitment',
            'coreHR',
          ],
          filterType: 'CHECKBOX',
          filterName: 'modularFunc',
          sort: true,
          isEnum: true,
          category: selectCategory,
          filterNameNice: 'catFilter.productModule',
          isOpenDefault: true,
        });
        categorySpecificFilter.push({
          id: 'mustHaveOrAdd',
          filterFields: [
            'selfService',
            'exitSurveys',
            'staffAppraisal',
            'timeReport',
          ],
          filterType: 'CHECKBOX',
          filterName: 'mustHaveOrAdd',
          isEnum: true,
          category: selectCategory,
          filterNameNice: 'catFilter.hrFunctions',
          isOpenDefault: true,
        });
        break;

      case allCategories?.HCM?.categoryDbName:
        categorySpecificFilter.push({
          id: 'modularFunc',
          filterFields: [
            'competence',
            'learningManagement',
            'compensationManagement',
            'performanceManagement',
            'successionPlanning',
            'hrAnalytics',
            'onboarding',
            'ownPayrollSystem',
            'recruitment',
            'coreHR',
          ],
          filterType: 'CHECKBOX',
          filterName: 'modularFunc',
          sort: true,
          isEnum: true,
          category: selectCategory,
          filterNameNice: 'catFilter.productModule',
          isOpenDefault: true,
        });
        categorySpecificFilter.push({
          id: 'mustHaveOrAdd',
          filterFields: [
            'selfService',
            'exitSurveys',
            'staffAppraisal',
            'timeReport',
          ],
          filterType: 'CHECKBOX',
          filterName: 'mustHaveOrAdd',
          isEnum: true,
          category: selectCategory,
          filterNameNice: 'catFilter.hrFunctions',
          isOpenDefault: true,
        });
        break;

      case allCategories?.BENEFITS?.categoryDbName:
        categorySpecificFilter.push({
          id: 'modularFunc',
          filterFields: [...filterFields],
          filterType: 'CHECKBOX',
          filterName: 'modularFunc',
          sort: true,
          isEnum: true,
          category: selectCategory,
          filterNameNice: 'catFilter.productFunctions',
          isOpenDefault: true,
        });
        categorySpecificFilter.push({
          id: 'benefitOffers',
          filterFields: ['wellnessBenefits', 'workLifeBalanceBenefits'],
          filterType: 'CHECKBOX',
          filterName: 'benefitOffers',
          isEnum: true,
          category: selectCategory,
          filterNameNice: 'catFilter.benefitOffers',
        });
        break;

      case allCategories?.TIMEREPORT?.categoryDbName:
      case allCategories?.TRAVELBILL?.categoryDbName:
      case allCategories?.WELLNESS?.categoryDbName:
      case allCategories?.MARKETING?.categoryDbName:
      case allCategories?.RECRUITMENT?.categoryDbName:
      case allCategories?.LMS?.categoryDbName:
      case allCategories?.ISSUE_TRACKING?.categoryDbName:
      case allCategories?.ACCOUNTING?.categoryDbName:
      case allCategories?.E_SIGN?.categoryDbName:
      case allCategories?.PROJECT_MANAGEMENT?.categoryDbName:
      case allCategories?.CASH_REGISTER?.categoryDbName:
        categorySpecificFilter.push({
          id: 'modularFunc',
          filterFields: [...filterFields],
          filterType: 'CHECKBOX',
          filterName: 'modularFunc',
          sort: true,
          isEnum: true,
          category: selectCategory,
          filterNameNice: 'catFilter.functions',
          isOpenDefault: true,
        });
        break;

      case allCategories?.ERP?.categoryDbName:
      case allCategories?.ERP_SYSTEM?.categoryDbName:
        categorySpecificFilter.push({
          id: 'modularFunc',
          filterFields: [
            'economy',
            'supplyChain',
            'crm',
            'projectManagement',
            'hrm',
          ],
          filterType: 'CHECKBOX',
          filterName: 'modularFunc',
          sort: true,
          isEnum: true,
          category: selectCategory,
          filterNameNice: 'catFilter.modules',
          isOpenDefault: true,
        });
        categorySpecificFilter.push({
          id: 'functionalities',
          filterFields: [
            'ecoAccounting',
            'ecoInvoice',
            'scPurchase',
            'scDelivery',
            'scManufacturing',
            'pmResourceManagement',
            'pmCapacityPlanning',
            'pmScheduling',
          ],
          filterType: 'CHECKBOX',
          filterName: 'functionalities',
          sort: true,
          isEnum: true,
          category: selectCategory,
          filterNameNice: 'catFilter.basicFunctions',
          isOpenDefault: true,
        });
        break;

      default:
        categorySpecificFilter.push(filterObj.deployment);

        categorySpecificFilter.push({
          id: 'modularFunc',
          filterFields: [...filterFields],
          filterType: 'CHECKBOX',
          filterName: 'modularFunc',
          sort: true,
          isEnum: true,
          category: selectCategory,
          filterNameNice: 'catFilter.functions',
          isOpenDefault: true,
        });
        break;
    }

    const allFilters = [...defaultFilters, ...categorySpecificFilter];

    if (excludedFilters) {
      const filteredFilters = allFilters.filter(
        (filterData) => !excludedFilters.includes(filterData.filterName)
      );

      setFilters(filteredFilters);
    } else {
      setFilters(allFilters);
    }
  }, [allCategories, category, excludedFilters]);

  useEffect(() => {
    let allSystemsCopy = JSON.parse(JSON.stringify(allSystems));

    // Get fields to filter data on.
    const filterArr = Object.keys(activeFilters).map((filterKey) => {
      const activeFiltersArr = filters.filter(
        (filterItem) => filterItem.filterName === filterKey
      )[0];

      return {
        filterFields: activeFiltersArr.filterFields,
        activeFields: activeFilters[filterKey],
        filterName: filterKey,
        isEnum: activeFiltersArr.isEnum,
      };
    });

    // Get keys for active filters
    const objectKeys = Object.keys(activeFilters);

    // Loop through activeFilters
    for (let i = 0; i < objectKeys.length; i += 1) {
      const filterItem = activeFilters[objectKeys[i]];

      const filterItemAdd =
        objectKeys[i] === 'mustHaveOrAdd'
          ? filterItem.map((item) => `${item}Add`)
          : [];

      const currentFilter = filterArr.filter(
        (item) => item.filterName === objectKeys[i]
      )[0];

      if (currentFilter.isEnum) {
        if (
          ['clientCompanySize', 'deployment', 'sector', 'branch'].includes(
            currentFilter.filterName
          )
        ) {
          const { filterName, activeFields } = currentFilter;

          if (activeFields.length) {
            allSystemsCopy = allSystemsCopy.filter((system) => {
              const systemField =
                system[filterName] ||
                system.systemProps?.[category]?.[filterName];

              if (!systemField) {
                return false;
              }

              if (typeof systemField === 'string') {
                return systemField === 'YES' || systemField === 'ADD';
              }

              return systemField.some((itemTest) => {
                if (filterName === 'sector') {
                  if (activeFields.includes('privateSector')) {
                    return systemField.includes('PRIVATE');
                  }
                  if (activeFields.includes('publicSector')) {
                    return systemField.includes('PUBLIC');
                  }
                }

                return activeFields.includes(itemTest);
              });
            });
          }
        } else {
          for (let j = 0; j < filterItem.length; j += 1) {
            const filterField = currentFilter.activeFields[j];
            const filterValue = currentFilter.activeFields;

            if (filterValue.length) {
              allSystemsCopy = allSystemsCopy.filter((system) => {
                // TODO: Find a solution for this
                const systemField =
                  system[filterField] ||
                  system.systemProps?.[category]?.[filterField];

                if (!systemField) {
                  return false;
                }

                if (typeof systemField === 'string') {
                  return systemField === 'YES' || systemField === 'ADD';
                }

                if (typeof systemField === 'boolean') {
                  return systemField;
                }

                return systemField.some((itemTest) =>
                  filterValue.includes(itemTest)
                );
              });
            }
          }
        }
      } else if (currentFilter.filterName === 'ratingBW') {
        const { filterName, activeFields } = currentFilter;

        allSystemsCopy = allSystemsCopy.filter(
          (system) => system[filterName] >= Number(activeFields)
        );
      } else if (filterItem.length) {
        for (let j = 0; j < filterItem.length; j += 1) {
          const col = filterItem[j];
          let colAdd = '';
          if (filterItemAdd.length) {
            colAdd = filterItemAdd[j];
          }

          // TODO: Find a solution for this
          allSystemsCopy = allSystemsCopy.filter(
            (item) =>
              item[col] ||
              item[colAdd] ||
              item.systemProps?.[category]?.[col] ||
              item.systemProps?.[category]?.[colAdd] ||
              item.systemAreas?.[category]?.[col]
          );
        }
      }
    }

    if (!allSystemsCopy) {
      return;
    }

    setSystems(allSystemsCopy);
  }, [activeFilters, filters, subCatFilter, category]);

  const handleCompare = useCallback(
    ({ id, systemName, systemUrl }) => {
      const array = [...compare];

      const index = compare.findIndex((c) => c.id === id);

      if (index > -1) {
        array.splice(index, 1);
      } else {
        array.push({ id, systemName, systemUrl });
      }

      setCompare(array);
    },
    [compare]
  );

  const handleFilterChange = useCallback(
    (name, value, type) => {
      // const { value, name, type } = e.target;
      let targetValue;

      if (type === 'radio') {
        targetValue = '';

        const filterHasValue =
          Object.keys(activeFilters).length &&
          activeFilters[name] &&
          activeFilters[name].length;

        if (filterHasValue && filterHasValue.length) {
          // Remove value
          targetValue = '';
        } else {
          // Add value
          targetValue = value;
        }
      } else if (type === 'checkbox') {
        const filterHasValue =
          Object.keys(activeFilters).length &&
          activeFilters[name] &&
          activeFilters[name].length;

        const currentValue = filterHasValue ? activeFilters[name] : [];

        targetValue = [];

        if (filterHasValue && activeFilters[name].includes(value)) {
          // Remove value
          const index = activeFilters[name].indexOf(value);
          if (index !== -1) activeFilters[name].splice(index, 1);
          targetValue = [...activeFilters[name]];
        } else {
          // Add value
          targetValue = [...currentValue, value];
        }
      }

      const filterItem = {
        ...activeFilters,
        [name]: targetValue,
      };

      setActiveFilters(filterItem);

      handleDataLayer({
        event_name: 'filter',
        filter_category: name,
        filter_value: value,
      });
    },
    [activeFilters, handleDataLayer]
  );

  const handleSubCatFilterChange = useCallback(
    (subCat) => {
      if (subCatFilter === subCat || subCat === 'clear') {
        setSubCatFilter(null);
      } else {
        setSubCatFilter(subCat);
      }
    },
    [subCatFilter]
  );

  const handleEmptyFilterItem = useCallback(
    (e) => {
      const { name } = e.currentTarget;

      const updatedActiveFilters = { ...activeFilters };
      delete updatedActiveFilters[name];
      setActiveFilters(updatedActiveFilters);
    },
    [activeFilters]
  );

  const handleSortChange = useCallback((id) => {
    if (id === 'rating') {
      setSystems((prevSystems) =>
        prevSystems.sort(sortSystemsAsc).sort(sortBwScore)
      );
    }

    if (id === 'popularity') {
      setSystems((prevSystems) =>
        prevSystems.sort(sortSystemsAsc).sort(sortRatingPopularity)
      );
    }

    setSorting(id);
  }, []);

  const emptyFilter = useCallback(() => {
    setActiveFilters({});
  }, []);

  return {
    compare,
    filters,
    activeFilters,
    allSystems,
    sorting,
    systems,
    subCatFilter,
    handleCompare,
    handleFilterChange,
    handleSubCatFilterChange,
    handleEmptyFilterItem,
    handleSortChange,
    emptyFilter,
  };
}
